import * as React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Util from "@lululemon/ecom-common/src/lib/util";
import { log } from "@lululemon/mwa-utils";
import { LinkGenerator } from "@lululemon/ecom-pattern-library-link-generator";
import RadioButtonTileGroup from "./radio-button-tile/RadioButtonTileGroup";
import { BraQuizDataConsumer } from "../../utils/quizDataContext";

import styles from "./quiz-question-tile.module.scss";

class QuizQuestionTile extends React.Component {
  static get propTypes() {
    const { shape, arrayOf, string, number, func, bool } = PropTypes;
    return {
      options: arrayOf(
        shape({
          caption: string,
          description: string.isRequired,
          value: number.isRequired,
          image: string,
          imageAlt: string,
        })
      ).isRequired,
      fieldValidationError: bool,
      isEmbedded: bool,
      isNotFirstStep: bool,
      onInputFocus: func.isRequired,
      onSaveAnswers: func.isRequired,
      questionID: string.isRequired,
      questionRef: func.isRequired,
      tabIndex: string.isRequired,
    };
  }

  static get defaultProps() {
    return {
      fieldValidationError: false,
      isEmbedded: false,
      isNotFirstStep: false,
    };
  }

  constructor() {
    super();
    this.validateData = this.validateData.bind(this);
    this.validateOptionsData = this.validateOptionsData.bind(this);
    this.onTileKeyDown = this.onTileKeyDown.bind(this);
    this.focusNextOption = this.focusNextOption.bind(this);
    this.focusPrevOption = this.focusPrevOption.bind(this);
    this.onTileOptionSelect = this.onTileOptionSelect.bind(this);
    this.removeTileAnswer = this.removeTileAnswer.bind(this);
    this.util = new Util("components/quiz-question-tile");
    this.state = {
      selectedAnswerIndex: null,
      entered: false,
      leaving: false,
    };

    this.optionsWithRef = [];
  }

  onTileOptionSelect(selectedOptionIndex) {
    const { options, questionID, onSaveAnswers } = this.props;
    this.setState({
      selectedAnswerIndex: selectedOptionIndex,
      entered: typeof selectedOptionIndex === "number",
    });

    onSaveAnswers(
      options[selectedOptionIndex].value,
      questionID,
      options[selectedOptionIndex].analyticsValue
    );
  }

  onTileKeyDown(e, index, radioGroupLength) {
    if (e.code === "Enter" || e.code === "Space") {
      e.preventDefault();
      this.onTileOptionSelect(index);
    }

    const lastRadioIndex = radioGroupLength - 1;

    // Left or Up arrow
    if (e.keyCode === 37 || e.keyCode === 38) {
      this.focusPrevOption(index, lastRadioIndex);
    }

    // Right or Down arrow
    if (e.keyCode === 39 || e.keyCode === 40) {
      this.focusNextOption(index, lastRadioIndex);
    }
  }

  focusPrevOption(index, radioLength) {
    const { onInputFocus } = this.props;
    const prevIndex = index === 0 ? radioLength : index - 1;
    prevIndex === 0
      ? onInputFocus()
      : this.optionsWithRef[prevIndex].ref.current.focus();
  }

  focusNextOption(index, radioLength) {
    const { onInputFocus } = this.props;
    const nextIndex = index === radioLength ? 0 : index + 1;
    nextIndex === 0
      ? onInputFocus()
      : this.optionsWithRef[nextIndex].ref.current.focus();
  }

  validateData() {
    const {
      options,
      questionID,
      onSaveAnswers,
      tabIndex,
      questionRef,
      onInputFocus,
    } = this.props;
    const rules = {
      options,
      questionID,
      onSaveAnswers,
      tabIndex,
      questionRef,
      onInputFocus,
    };
    const required = this.util.requiredData(rules);
    if (!required.pass) {
      log.warning("Missing required data", required.missing);
    }
    return required;
  }

  validateOptionsData() {
    const { options } = this.props;

    const optionData =
      options &&
      options.map(
        ({ description, value }) => !!description && typeof value === "number"
      );
    const failedValidation = options && optionData.includes(false);

    if (failedValidation) {
      log.warning(
        "Missing required data:",
        "each option in options must include an image, imageAlt, caption, description and value"
      );
    }

    return failedValidation;
  }

  removeTileAnswer() {
    const { onSaveAnswers, questionID, onInputFocus } = this.props;

    this.setState(
      {
        entered: false,
        leaving: true,
      },
      () => {
        onSaveAnswers(null, questionID);
      }
    );

    this.setState(
      {
        leaving: false,
        selectedAnswerIndex: null,
      },
      () => {
        onInputFocus();
      }
    );
  }

  render() {
    if (!this.validateData().pass || this.validateOptionsData()) return null;

    const {
      options,
      questionID,
      tabIndex,
      questionRef,
      isNotFirstStep,
      isEmbedded,
      fieldValidationError,
    } = this.props;
    const { selectedAnswerIndex, entered, leaving } = this.state;

    // Add a ref to each option, to access the inputs for keyboard/tabbing
    // Use questionRef from parent for first input. For focusing on first input when slide changes
    this.optionsWithRef = options.map((option, index) => {
      const ref = index === 0 ? questionRef : React.createRef();
      return { option, ref };
    });

    return (
      <BraQuizDataConsumer>
        {({ questions }) => {
          const {
            general: { changeMyAnswer },
          } = questions;
          return (
            <div
              className={classNames("row", styles.quizQuestionTile, {
                [styles.quizQuestionTileMargin]: isNotFirstStep,
                [styles.quizQuestionTileWithSelection]:
                  typeof selectedAnswerIndex === "number",
                [styles.quizQuestionTileFirstTileSelected]:
                  selectedAnswerIndex === 0,
                [styles.quizQuestionTileEntered]: entered,
                [styles.quizQuestionTileLeaving]: leaving,
                [styles.quizQuestionTileEmbedded]: isEmbedded,
              })}
            >
              <RadioButtonTileGroup
                options={this.optionsWithRef}
                selectedAnswerIndex={selectedAnswerIndex}
                onKeyDown={this.onTileKeyDown}
                onSelect={this.onTileOptionSelect}
                classes={{
                  option: classNames({
                    [styles.quizQuestionTileOption]: !isEmbedded,
                  }),
                  options: classNames({
                    [styles.quizQuestionTileOptions]: !isEmbedded,
                  }),
                }}
                imagePrefix="bra-quiz-question"
                liveRegion={`quiz-question-tile__confirmation-${questionID}`}
                questionID={questionID}
                tabIndex={tabIndex}
                entered={entered}
                leaving={leaving}
                isEmbedded={isEmbedded}
                fieldValidationError={fieldValidationError}
              />
              {/* aria live region should exist on the page whether there is content or not */}
              <div
                className={classNames(styles.quizQuestionTileConfirmation, {
                  [styles.quizQuestionTileConfirmationEmbedded]: isEmbedded,
                })}
                role="region"
                id={`quiz-question-tile__confirmation-${questionID}`}
                aria-live="polite"
                data-testid="quiz-question-tile-confirmation"
                aria-hidden={selectedAnswerIndex === null}
              >
                {this.optionsWithRef.map((item, index) => {
                  const isSelectedAnswer = selectedAnswerIndex === index;
                  const {
                    option: { description },
                  } = item;
                  return isSelectedAnswer && !leaving ? (
                    <React.Fragment key={description}>
                      <p
                        className={classNames(
                          styles.quizQuestionTileConfirmationText,
                          {
                            [styles.quizQuestionTileConfirmationTextEmbedded]:
                              isEmbedded,
                          }
                        )}
                      >
                        {description}
                      </p>
                      <LinkGenerator
                        href="#"
                        role="link"
                        className={classNames(
                          styles.quizQuestionTileConfirmationButton,
                          {
                            [styles.quizQuestionTileConfirmationButtonEmbedded]:
                              isEmbedded,
                          }
                        )}
                        onClick={this.removeTileAnswer}
                        tabindex={tabIndex}
                      >
                        {changeMyAnswer}
                      </LinkGenerator>
                    </React.Fragment>
                  ) : null;
                })}
              </div>
            </div>
          );
        }}
      </BraQuizDataConsumer>
    );
  }
}

export default QuizQuestionTile;
